import React, { useState } from 'react'
import AppExcelUploadView from '../../../components/AppExcelUploadView'
import {
  uploadStudentExcel,
  downloadSampleTemplate,
  uploadFeesExcel,
  downloadFeesSampleTemplate,
} from './api/uploadExcelApi'
import { appToast } from '../../../components/AppToast'

export default function UploadExcelListScreen() {
  const [activeTab, setActiveTab] = useState('students')
  const [lastResult, setLastResult] = useState(null)

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setLastResult(null)
  }

  const handleDownloadStudentTemplate = async () => {
    try {
      await downloadSampleTemplate('students')
      appToast.success('Student upload template downloaded successfully.')
    } catch (err) {
      console.error(err)
      appToast.error('Failed to download student template.')
    }
  }

  const handleDownloadFeesTemplate = async () => {
    try {
      await downloadFeesSampleTemplate()
      appToast.success('Fees upload template downloaded successfully.')
    } catch (err) {
      console.error(err)
      appToast.error('Failed to download fees template.')
    }
  }

  const handleUploadStudents = async (file) => {
    try {
      const res = await uploadStudentExcel(file)
      setLastResult({ ...res, uploadType: 'students', fileName: file.name })
      if (res.failed_count > 0 && res.imported_count === 0) {
        const firstErr = res.errors?.[0]?.message || 'Bulk student import failed.'
        appToast.error(firstErr)
        throw new Error(firstErr)
      } else if (res.failed_count > 0) {
        appToast.warning(
          `Imported ${res.imported_count} student(s) with ${res.failed_count} error(s). Review summary below.`
        )
      } else {
        appToast.success(res?.message || `Successfully imported ${res.imported_count} student record(s)!`)
      }
      return res
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to import student spreadsheet.'
      appToast.error(detail)
      throw err
    }
  }

  const handleUploadFees = async (file) => {
    try {
      const res = await uploadFeesExcel(file)
      setLastResult({ ...res, uploadType: 'fees', fileName: file.name })
      if (res.failed_count > 0 && res.imported_count === 0) {
        const firstErr = res.errors?.[0]?.message || 'Bulk fees import failed.'
        appToast.error(firstErr)
        throw new Error(firstErr)
      } else if (res.failed_count > 0) {
        appToast.warning(
          `Imported ${res.imported_count} fee installment(s) with ${res.failed_count} error(s). Review summary below.`
        )
      } else {
        appToast.success(res?.message || `Successfully imported ${res.imported_count} fee installment(s)!`)
      }
      return res
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to import fees spreadsheet.'
      appToast.error(detail)
      throw err
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Top Header & SubNav Canopy ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant mb-1">
            <span>Admission Module</span>
            <span>/</span>
            <span>Operations &amp; Services</span>
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Bulk Excel Importer
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant">
            Import student registrations, enrollments, and fee structures from standardized Excel spreadsheets.
          </p>
        </div>

        {/* ── Type Selector Tabs ── */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleTabChange('students')}
            className={`flex items-center gap-2 rounded-2xl px-5 py-2.5 text-xs font-bold transition shadow-2xs cursor-pointer ${
              activeTab === 'students'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-base">school</span>
            <span>Upload Students</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('fees')}
            className={`flex items-center gap-2 rounded-2xl px-5 py-2.5 text-xs font-bold transition shadow-2xs cursor-pointer ${
              activeTab === 'fees'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-base">receipt_long</span>
            <span>Upload Fees</span>
          </button>
        </div>
      </section>

      {/* ── Recent Execution Feedback Banner ── */}
      {lastResult && (
        <div
          className={`rounded-2xl border p-5 shadow-xs transition ${
            lastResult.failed_count === 0
              ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-950 dark:text-emerald-100'
              : lastResult.imported_count > 0
              ? 'border-amber-500/30 bg-amber-500/5 text-amber-950 dark:text-amber-100'
              : 'border-rose-500/30 bg-rose-500/5 text-rose-950 dark:text-rose-100'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span
                className={`material-symbols-outlined text-2xl ${
                  lastResult.failed_count === 0
                    ? 'text-emerald-600'
                    : lastResult.imported_count > 0
                    ? 'text-amber-600'
                    : 'text-rose-600'
                }`}
              >
                {lastResult.failed_count === 0
                  ? 'check_circle'
                  : lastResult.imported_count > 0
                  ? 'warning'
                  : 'error'}
              </span>
              <div>
                <h4 className="text-sm font-bold">
                  Import Summary for &quot;{lastResult.fileName}&quot;
                </h4>
                <p className="mt-0.5 text-xs opacity-90">{lastResult.message}</p>

                {/* Metric Badges */}
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-surface-container-high px-2.5 py-1 font-semibold text-on-surface">
                    <span className="text-on-surface-variant">Total Rows:</span>
                    <strong className="text-on-surface">{lastResult.total_rows}</strong>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/15 px-2.5 py-1 font-semibold text-emerald-700 dark:text-emerald-300">
                    <span>Imported:</span>
                    <strong>{lastResult.imported_count}</strong>
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 font-semibold ${
                      lastResult.failed_count > 0
                        ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                        : 'bg-surface-container-high text-on-surface-variant'
                    }`}
                  >
                    <span>Failed / Skipped:</span>
                    <strong>{lastResult.failed_count}</strong>
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setLastResult(null)}
              className="text-on-surface-variant hover:text-on-surface transition p-1 cursor-pointer"
              title="Dismiss Summary"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
          </div>

          {/* Row-by-Row Error Detail Table */}
          {Array.isArray(lastResult.errors) && lastResult.errors.length > 0 && (
            <div className="mt-4 overflow-hidden rounded-xl border border-rose-500/20 bg-surface-container-lowest shadow-2xs">
              <div className="bg-rose-500/10 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300">
                Detailed Row Errors ({lastResult.errors.length})
              </div>
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-surface-container-low text-[11px] font-bold text-on-surface-variant">
                    <tr>
                      <th className="px-4 py-2">Row</th>
                      <th className="px-4 py-2">Field</th>
                      <th className="px-4 py-2">Validation Failure Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/10 font-medium text-on-surface">
                    {lastResult.errors.map((err, idx) => (
                      <tr key={idx} className="hover:bg-surface-container-low/50">
                        <td className="px-4 py-2 font-mono text-[11px] text-rose-600 font-bold">
                          #{err.row}
                        </td>
                        <td className="px-4 py-2 text-on-surface-variant font-semibold">
                          {err.field || 'General'}
                        </td>
                        <td className="px-4 py-2 text-rose-700 dark:text-rose-300">
                          {err.message}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Shared Upload Card ── */}
      {activeTab === 'students' ? (
        <AppExcelUploadView
          title="Upload Students"
          subtitle="Import student bio-data, academic enrollments, and parent contact information."
          description="Use this option to upload Students using Excel Sheets in 3 simple steps. Download a sample file, fill details and upload the file. Please make sure that all Master Values (Ex. Branch, Course, Batch, Gender) are selected properly."
          sampleFileName="Students_Upload_Template.xlsx"
          cancelPath="/admission/students/list"
          backLabel="Back to Students List"
          masterRequirements={['Institute', 'Branch', 'Course', 'Batch', 'Category / Quota', 'Gender']}
          onDownloadSample={handleDownloadStudentTemplate}
          onUpload={handleUploadStudents}
        />
      ) : (
        <AppExcelUploadView
          title="Upload Fees &amp; Installments"
          subtitle="Import fee schedules, installment amounts, and concessions."
          description="Use this option to upload Fees using Excel Sheets in 3 simple steps. Download a sample file, fill details and upload the file. Please make sure that all Master Values (Ex. Fee Head, Academic Year, Scheme) are configured properly."
          sampleFileName="Fees_Structure_Template.xlsx"
          cancelPath="/admission/fees/list"
          backLabel="Back to Fees List"
          masterRequirements={['Academic Year', 'Fee Head', 'Installment Scheme', 'Course']}
          onDownloadSample={handleDownloadFeesTemplate}
          onUpload={handleUploadFees}
        />
      )}
    </div>
  )
}
